import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function AutomationControls() {
  const { t } = useTranslation();
  const [shutterVideoIndex, setShutterVideoIndex] = useState(0);

  const exteriorFeatures = [
    t('automation_exterior_1'),
    t('automation_exterior_2'),
    t('automation_exterior_3'),
    t('automation_exterior_4'),
    t('automation_exterior_5')
  ];

  const interiorFeatures = [
    t('automation_interior_1'),
    t('automation_interior_2'),
    t('automation_interior_3'),
    t('automation_interior_4')
  ];

  const shutterVideos = [
    { 
      url: 'https://rgyfmccwtgvwanuedayh.supabase.co/storage/v1/object/public/tapparelle/Tapparelle%20(1)%20(1).mov',
      description: t('automation_video_internal') 
    },
    { 
      url: 'https://rgyfmccwtgvwanuedayh.supabase.co/storage/v1/object/public/tapparelle%20app/Tapparelle%20e%20app%20(1)%20(1).mov',
      description: t('automation_video_internal_app') 
    }
  ];

  return (
    <section className="py-24 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold mb-12 text-gray-900">
          {t('automation_title')}
        </h2>

        <p className="text-gray-700 text-lg leading-relaxed mb-12">
          {t('automation_intro')}
        </p>

        {/* Tapparelle (Interior Blinds) - Vertical Video with Carousel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
          {/* Left Column: Text Content */}
          <div className="flex flex-col justify-start">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('automation_interior_title')}
            </h3>
            
            <div className="space-y-4">
              {interiorFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-lg leading-relaxed flex-1">
                    {feature}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Video Carousel - Vertical */}
          <div className="w-full">
            <div className="relative w-full h-[600px] rounded-2xl overflow-hidden bg-black flex items-center justify-center">
              <video
                src={shutterVideos[shutterVideoIndex].url}
                controls
                className="w-full h-full object-contain"
                playsInline
              >
                Your browser does not support the video tag.
              </video>
              
              {/* Carousel Controls */}
              <button
                onClick={() => setShutterVideoIndex((prev) => (prev - 1 + shutterVideos.length) % shutterVideos.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors cursor-pointer z-10"
                aria-label="Previous video"
              >
                <i className="ri-arrow-left-s-line text-white text-2xl"></i>
              </button>
              <button
                onClick={() => setShutterVideoIndex((prev) => (prev + 1) % shutterVideos.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors cursor-pointer z-10"
                aria-label="Next video"
              >
                <i className="ri-arrow-right-s-line text-white text-2xl"></i>
              </button>
              
              {/* Carousel Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {shutterVideos.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setShutterVideoIndex(idx)}
                    className={`h-2 rounded-full transition-all cursor-pointer ${
                      idx === shutterVideoIndex ? 'bg-white w-8' : 'bg-white/50 w-2'
                    }`}
                    aria-label={`Go to video ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-center text-gray-600 mt-4 text-base italic">
              {shutterVideos[shutterVideoIndex].description}
            </p>
          </div>
        </div>

        {/* Tende Esterne (Exterior Blinds) - Horizontal Video */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
          {/* Left Column: Text Content */}
          <div className="flex flex-col justify-start">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('automation_exterior_title')}
            </h3>
            
            <div className="space-y-4">
              {exteriorFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-lg leading-relaxed flex-1">
                    {feature}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Video - Horizontal */}
          <div className="w-full">
            <div className="w-full h-[400px] rounded-2xl overflow-hidden bg-black relative">
              <video
                src="https://rgyfmccwtgvwanuedayh.supabase.co/storage/v1/object/public/tende%20est/Tende%20esterne%20(1).mov"
                controls
                className="w-full h-full object-contain"
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-gray-600 mt-4 text-base italic">
              {t('automation_video_external')}
            </p>
          </div>
        </div>

        {/* Zanzariere (Screens) - Horizontal Video */}
        <div className="w-full">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            {t('automation_video_screens')}
          </h3>
          <div className="w-full h-[500px] rounded-2xl overflow-hidden bg-black relative">
            <video
              src="https://rgyfmccwtgvwanuedayh.supabase.co/storage/v1/object/public/zanz/Zanzariere.mov"
              controls
              className="w-full h-full object-contain"
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-center text-gray-600 mt-4 text-base italic">
            {t('automation_video_screens')}
          </p>
        </div>
      </div>
    </section>
  );
}
